import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import TreatmentPlanService from '../../../services/treatmentPlanService';
import PatientService from '../../../services/patientService';
import ProfileService from '../../../services/profileService';
import ProcedureService from '../../../services/procedureService';
import OrganizationService from '../../../services/organizationService';
import { regimenFiscalCatalog, usoCfdiCatalog } from '../../../utils/satCatalogs';
import './TreatmentPlanInvoice.css';

const SAT_PAYMENT_METHODS = [
  { code: '01', name: 'Efectivo' },
  { code: '02', name: 'Cheque nominativo' },
  { code: '03', name: 'Transferencia electrónica de fondos' },
  { code: '04', name: 'Tarjeta de crédito' },
  { code: '05', name: 'Monedero electrónico' },
  { code: '06', name: 'Dinero electrónico' },
  { code: '08', name: 'Vales de despensa' },
  { code: '12', name: 'Dación en pago' },
  { code: '13', name: 'Pago por subrogación' },
  { code: '14', name: 'Pago por consignación' },
  { code: '15', name: 'Condonación' },
  { code: '17', name: 'Compensación' },
  { code: '23', name: 'Novación' },
  { code: '24', name: 'Confusión' },
  { code: '25', name: 'Remisión de deuda' },
  { code: '26', name: 'Prescripción o caducidad' },
  { code: '27', name: 'A satisfacción del acreedor' },
  { code: '28', name: 'Tarjeta de débito' },
  { code: '29', name: 'Tarjeta de servicios' },
  { code: '30', name: 'Aplicación de anticipos' },
  { code: '31', name: 'Intermediario pagos' },
  { code: '99', name: 'Por definir' }
];

const SAT_PAYMENT_METHOD_TYPES = [
  { code: 'PUE', name: 'Pago en una sola exhibición' },
  { code: 'PPD', name: 'Pago en parcialidades o diferido' }
];

const TreatmentPlanInvoice = () => {
  const { patientId, planId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [emitterInfo, setEmitterInfo] = useState({
    rfc: '',
    nombre: '',
    regimenFiscal: '',
    codigoPostal: ''
  });
  const [billingSerie, setBillingSerie] = useState('');
  const [billingNextFolio, setBillingNextFolio] = useState('');
  const [invoiceDetails, setInvoiceDetails] = useState(null);

  const emitterRegimenLabel = useMemo(() => {
    if (!emitterInfo.regimenFiscal) return '';
    const match = regimenFiscalCatalog.find(item => item.codigo === emitterInfo.regimenFiscal);
    return match ? `${match.codigo} - ${match.descripcion}` : emitterInfo.regimenFiscal;
  }, [emitterInfo.regimenFiscal]);

  const [patient, setPatient] = useState(null);
  const [plan, setPlan] = useState(null);
  const [procedureCatalog, setProcedureCatalog] = useState({});

  const [formData, setFormData] = useState({
    receptorRfc: '',
    receptorNombre: '',
    receptorRegimenFiscal: '',
    receptorUsoCfdi: '',
    receptorCodigoPostal: '',
    receptorEmail: '',
    serie: '',
    folio: '',
    formaPago: '99',
    metodoPago: 'PUE',
    lugarExpedicion: ''
  });

  const receptorRegimenLabel = useMemo(() => {
    if (!formData.receptorRegimenFiscal) return '';
    const match = regimenFiscalCatalog.find(item => item.codigo === formData.receptorRegimenFiscal);
    return match ? `${match.codigo} - ${match.descripcion}` : formData.receptorRegimenFiscal;
  }, [formData.receptorRegimenFiscal]);

  const receptorUsoLabel = useMemo(() => {
    if (!formData.receptorUsoCfdi) return '';
    const match = usoCfdiCatalog.find(item => item.codigo === formData.receptorUsoCfdi);
    return match ? `${match.codigo} - ${match.descripcion}` : formData.receptorUsoCfdi;
  }, [formData.receptorUsoCfdi]);

  const formaPagoLabel = useMemo(() => {
    const match = SAT_PAYMENT_METHODS.find(item => item.code === formData.formaPago);
    return match ? `${match.code} - ${match.name}` : formData.formaPago;
  }, [formData.formaPago]);

  const metodoPagoLabel = useMemo(() => {
    const match = SAT_PAYMENT_METHOD_TYPES.find(item => item.code === formData.metodoPago);
    return match ? `${match.code} - ${match.name}` : formData.metodoPago;
  }, [formData.metodoPago]);

  const currencyFormatter = useMemo(() => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
  }, []);

  const formatTaxLabel = (taxConfig, label) => {
    if (!taxConfig?.enabled) {
      return null;
    }
    const typeLabel = taxConfig.type === 'retencion' ? 'Retención' : 'Traslado';
    if (taxConfig.rate === 'exento') {
      return `${label} (${typeLabel}): Exento`;
    }
    if (taxConfig.rate === null || taxConfig.rate === undefined || taxConfig.rate === '') {
      return null;
    }
    return `${label} (${typeLabel}): ${taxConfig.rate}%`;
  };

  const getLineTotal = (proc) => Number(proc.total || 0);

  const getTaxRates = (taxes) => {
    const ivaRate = taxes?.iva?.enabled && taxes.iva.rate !== 'exento' && taxes.iva.rate !== null && taxes.iva.rate !== undefined && taxes.iva.rate !== ''
      ? Number(taxes.iva.rate || 0) / 100
      : 0;
    const isrRate = taxes?.isr?.enabled && taxes.isr.rate !== 'exento' && taxes.isr.rate !== null && taxes.isr.rate !== undefined && taxes.isr.rate !== ''
      ? Number(taxes.isr.rate || 0) / 100
      : 0;
    return { ivaRate, isrRate };
  };

  const getLineBase = (proc, taxes) => {
    const total = getLineTotal(proc);
    const { ivaRate, isrRate } = getTaxRates(taxes);
    const denominator = 1 + ivaRate - isrRate;
    if (denominator <= 0) return total;
    return total / denominator;
  };

  const getLineListSubtotal = (proc, taxes) => {
    const quantity = Math.max(Number(proc.quantity || 0), 0);
    const unitPrice = Number(proc.amount);

    if (Number.isFinite(unitPrice) && unitPrice > 0) {
      return quantity * unitPrice;
    }

    return getLineBase(proc, taxes);
  };

  const getLineDiscount = (proc, taxes) => {
    const listSubtotal = getLineListSubtotal(proc, taxes);
    const discountedSubtotal = getLineBase(proc, taxes);
    return Math.max(0, listSubtotal - discountedSubtotal);
  };

  const getLineTaxAmount = (proc, taxConfig, taxes) => {
    if (!taxConfig?.enabled) return 0;
    if (taxConfig.rate === 'exento' || taxConfig.rate === null || taxConfig.rate === undefined || taxConfig.rate === '') return 0;
    const rate = Number(taxConfig.rate || 0) / 100;
    const base = Math.max(0, getLineBase(proc, taxes));
    return base * rate;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!auth.user?.access_token) return;
        const token = auth.user.access_token;

        const [patientData, planResponse, profileData] = await Promise.all([
          PatientService.getPatientById(patientId, token),
          TreatmentPlanService.getTreatmentPlanById(planId, token),
          ProfileService.getUserProfile(auth.user.profile.sub, token)
        ]);

        const planData = planResponse?.data || null;

        let featureEnabled = false;
        let organization = null;
        if (profileData?.organizationId) {
          organization = await OrganizationService.getOrganizationById(profileData.organizationId, token);
          featureEnabled = Boolean(organization?.tags?.includes('fe_enabled'));
        }

        let catalogMap = {};
        const procedureIds = Array.isArray(planData?.selectedProcedures)
          ? [...new Set(planData.selectedProcedures.map(proc => proc.procedureId).filter(Boolean))]
          : [];

        if (procedureIds.length > 0) {
          const catalogItems = await Promise.all(
            procedureIds.map(procId => ProcedureService.getProcedureById(procId, token).catch(() => null))
          );

          catalogMap = catalogItems.reduce((acc, item) => {
            if (item?.id) {
              acc[item.id] = item;
            }
            return acc;
          }, {});
        }

        setPatient(patientData);
        setPlan(planData);
        setProcedureCatalog(catalogMap);
        setIsFeatureEnabled(featureEnabled);
        setEmitterInfo({
          rfc: organization?.fiscalId || profileData?.rfc || '',
          nombre: organization?.billingEmitterName || organization?.name || profileData?.name || '',
          regimenFiscal: profileData?.taxRegime || '',
          codigoPostal: profileData?.zipCode || ''
        });
        setBillingSerie(organization?.billingSerie || '');
        setBillingNextFolio(Number.isInteger(organization?.billingNextFolio) ? String(organization.billingNextFolio) : '1');
        const facturacion = patientData?.facturacion || {};
        setFormData(prev => ({
          ...prev,
          receptorRfc: facturacion.rfc || '',
          receptorNombre: facturacion.nombreReceptor || `${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim(),
          receptorRegimenFiscal: facturacion.regimenFiscal || '',
          receptorUsoCfdi: facturacion.usoCfdi || '',
          receptorCodigoPostal: facturacion.codigoPostal || '',
          receptorEmail: patientData?.email || '',
          folio: planData?.id || '',
          lugarExpedicion: profileData?.zipCode || ''
        }));
      } catch (error) {
        console.error('Error loading invoice data:', error);
        setMessage({ type: 'error', text: 'Error al cargar la información de facturación' });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [auth.user, patientId, planId]);

  useEffect(() => {
    if (!plan?.satInvoice?.cfdi) return;

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(plan.satInvoice.cfdi, 'text/xml');
      const comprobante = xmlDoc.getElementsByTagName('cfdi:Comprobante')[0] || xmlDoc.getElementsByTagName('Comprobante')[0];
      const emisor = xmlDoc.getElementsByTagName('cfdi:Emisor')[0] || xmlDoc.getElementsByTagName('Emisor')[0];
      const receptor = xmlDoc.getElementsByTagName('cfdi:Receptor')[0] || xmlDoc.getElementsByTagName('Receptor')[0];

      const serie = comprobante?.getAttribute('Serie') || '';
      const folio = comprobante?.getAttribute('Folio') || '';
      const formaPago = comprobante?.getAttribute('FormaPago') || '';
      const metodoPago = comprobante?.getAttribute('MetodoPago') || '';
      const lugarExpedicion = comprobante?.getAttribute('LugarExpedicion') || '';

      const emisorRfc = emisor?.getAttribute('Rfc') || '';
      const emisorNombre = emisor?.getAttribute('Nombre') || '';
      const emisorRegimen = emisor?.getAttribute('RegimenFiscal') || '';

      const receptorRfc = receptor?.getAttribute('Rfc') || '';
      const receptorNombre = receptor?.getAttribute('Nombre') || '';
      const receptorRegimen = receptor?.getAttribute('RegimenFiscalReceptor') || '';
      const receptorUso = receptor?.getAttribute('UsoCFDI') || '';
      const receptorCp = receptor?.getAttribute('DomicilioFiscalReceptor') || '';

      const total = comprobante?.getAttribute('Total') || '';
      const moneda = comprobante?.getAttribute('Moneda') || '';
      const fecha = comprobante?.getAttribute('Fecha') || '';

      if (serie) setBillingSerie(serie);
      if (folio) setBillingNextFolio(folio);
      setEmitterInfo(prev => ({
        rfc: emisorRfc || prev.rfc,
        nombre: emisorNombre || prev.nombre,
        regimenFiscal: emisorRegimen || prev.regimenFiscal,
        codigoPostal: prev.codigoPostal
      }));
      setInvoiceDetails({
        uuid: plan?.satInvoice?.uuid || '',
        fechaTimbrado: plan?.satInvoice?.fechaTimbrado || '',
        serie: serie || '',
        folio: folio || '',
        total,
        moneda,
        fecha
      });
      setFormData(prev => ({
        ...prev,
        folio: folio || prev.folio,
        formaPago: formaPago || prev.formaPago,
        metodoPago: metodoPago || prev.metodoPago,
        lugarExpedicion: lugarExpedicion || prev.lugarExpedicion,
        receptorRfc: receptorRfc || prev.receptorRfc,
        receptorNombre: receptorNombre || prev.receptorNombre,
        receptorRegimenFiscal: receptorRegimen || prev.receptorRegimenFiscal,
        receptorUsoCfdi: receptorUso || prev.receptorUsoCfdi,
        receptorCodigoPostal: receptorCp || prev.receptorCodigoPostal
      }));
    } catch (error) {
      console.error('Error parsing CFDI XML:', error);
    }
  }, [plan?.satInvoice?.cfdi, plan?.satInvoice?.uuid, plan?.satInvoice?.fechaTimbrado]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const getNormalizedReceptor = () => ({
    rfc: (formData.receptorRfc || '').trim().toUpperCase(),
    nombreReceptor: (formData.receptorNombre || '').trim(),
    regimenFiscal: formData.receptorRegimenFiscal || '',
    usoCfdi: formData.receptorUsoCfdi || '',
    codigoPostal: (formData.receptorCodigoPostal || '').trim(),
    email: formData.receptorEmail.trim()
  });

  const ensureInvoiceGenerated = async () => {
    if (plan?.satInvoice?.cfdi) return;

    if (!formData.receptorRfc || !formData.receptorNombre || !formData.receptorRegimenFiscal || !formData.receptorUsoCfdi || !formData.receptorCodigoPostal) {
      throw new Error('Completa la información fiscal del paciente');
    }

    const token = auth.user?.access_token;
    const normalized = getNormalizedReceptor();

    const currentFacturacion = patient?.facturacion || {};
    const hasDifferences =
      (currentFacturacion.rfc || '').toUpperCase() !== normalized.rfc ||
      (currentFacturacion.nombreReceptor || '').trim() !== normalized.nombreReceptor ||
      (currentFacturacion.regimenFiscal || '') !== normalized.regimenFiscal ||
      (currentFacturacion.usoCfdi || '') !== normalized.usoCfdi ||
      (currentFacturacion.codigoPostal || '').trim() !== normalized.codigoPostal;

    if (hasDifferences) {
      const shouldUpdate = window.confirm('Los datos fiscales del receptor son diferentes a los guardados. ¿Deseas actualizar la información fiscal del paciente?');
      if (shouldUpdate && patient?.id) {
        await PatientService.updatePatient(patient.id, {
          facturacion: {
            rfc: normalized.rfc,
            nombreReceptor: normalized.nombreReceptor,
            regimenFiscal: normalized.regimenFiscal,
            usoCfdi: normalized.usoCfdi,
            codigoPostal: normalized.codigoPostal
          }
        }, token);
      }
    }

    const payload = {
      receptor: {
        rfc: normalized.rfc,
        nombre: normalized.nombreReceptor,
        regimenFiscal: normalized.regimenFiscal,
        usoCfdi: normalized.usoCfdi,
        codigoPostal: normalized.codigoPostal,
        email: normalized.email
      },
      comprobante: {
        serie: billingSerie || undefined,
        folio: billingNextFolio || formData.folio.trim(),
        formaPago: formData.formaPago,
        metodoPago: formData.metodoPago,
        lugarExpedicion: formData.lugarExpedicion.trim()
      }
    };

    await TreatmentPlanService.issueTreatmentPlanInvoice(planId, payload, token);

    const refreshed = await TreatmentPlanService.getTreatmentPlanById(planId, token);
    setPlan(refreshed?.data || plan);
  };

  const downloadInvoiceFile = async (type) => {
    setMessage({ type: '', text: '' });
    try {
      setIsSubmitting(true);
      if (!plan?.invoicedBySat || !plan?.satInvoice?.cfdi) {
        throw new Error('La factura aún no ha sido timbrada');
      }

      const token = auth.user?.access_token;
      const blob = type === 'xml'
        ? await TreatmentPlanService.downloadTreatmentPlanInvoiceXml(planId, token)
        : await TreatmentPlanService.downloadTreatmentPlanInvoicePdf(planId, token);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cfdi-${plan?.satInvoice?.uuid || planId}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setMessage({ type: 'error', text: error.message || 'Error al descargar la factura' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setMessage({ type: '', text: '' });
    try {
      setIsSubmitting(true);
      await ensureInvoiceGenerated();
      setMessage({ type: 'success', text: 'Factura generada correctamente' });
    } catch (error) {
      console.error('Error generating invoice:', error);
      setMessage({ type: 'error', text: error.message || 'Error al generar la factura' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const procedures = plan?.selectedProcedures || [];
  const hasMissingCodigoSat = procedures.some(proc => {
    const codigoSAT = procedureCatalog[proc.procedureId]?.codigoSAT;
    return !codigoSAT || codigoSAT.trim().length === 0;
  });
  const isReadOnly = plan?.invoicedBySat;
  const totals = procedures.reduce((acc, proc) => {
    const taxes = procedureCatalog[proc.procedureId]?.taxes || {};
    const listSubtotal = getLineListSubtotal(proc, taxes);
    const discount = getLineDiscount(proc, taxes);
    const ivaAmount = getLineTaxAmount(proc, taxes.iva, taxes);
    const isrAmount = getLineTaxAmount(proc, taxes.isr, taxes);
    acc.subtotal += listSubtotal;
    acc.discount += discount;
    acc.iva += ivaAmount;
    acc.isr += isrAmount;
    acc.total += getLineTotal(proc);
    return acc;
  }, { subtotal: 0, discount: 0, iva: 0, isr: 0, total: 0 });
  const isSubmitDisabled = isSubmitting || procedures.length === 0 || hasMissingCodigoSat;
  const isDownloadDisabled = isSubmitting || !plan?.invoicedBySat || !plan?.satInvoice?.cfdi;
  const isGenerateDisabled = isSubmitDisabled || plan?.invoicedBySat;

  if (isLoading) {
    return (
      <div className="treatment-plan-invoice-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isFeatureEnabled) {
    return (
      <div className="treatment-plan-invoice-container">
        <div className="invoice-card">
          <h3>Funcionalidad no disponible</h3>
          <p>La facturación electrónica no está habilitada para tu organización actualmente.</p>
          <p>Por favor contacta a soporte (soporte@digitaport.com) para solicitar la activación de este módulo.</p>
          <div className="invoice-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(`/patients/${patientId}/treatment-plans`)}
            >
              Volver
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="treatment-plan-invoice-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : undefined}
        sectionTitle={plan?.invoicedBySat ? 'Descargar Factura' : 'Generar Factura'}
        onBackClick={() => navigate(`/patients/${patientId}/treatment-plans`)}
      />

      <div className="invoice-content">
        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}

        {plan?.invoicedBySat && invoiceDetails ? (
          <div className="invoice-card">
            <h3>Detalles del CFDI</h3>
            <div className="invoice-grid">
              <div className="form-group">
                <label>UUID</label>
                <div className="invoice-value">{invoiceDetails.uuid || '—'}</div>
              </div>
              <div className="form-group">
                <label>Fecha de timbrado</label>
                <div className="invoice-value">{invoiceDetails.fechaTimbrado || '—'}</div>
              </div>
              <div className="form-group">
                <label>Serie</label>
                <div className="invoice-value">{invoiceDetails.serie || '—'}</div>
              </div>
              <div className="form-group">
                <label>Folio</label>
                <div className="invoice-value">{invoiceDetails.folio || '—'}</div>
              </div>
              <div className="form-group">
                <label>Fecha de emisión</label>
                <div className="invoice-value">{invoiceDetails.fecha || '—'}</div>
              </div>
              <div className="form-group">
                <label>Total</label>
                <div className="invoice-value">
                  {invoiceDetails.total ? `${invoiceDetails.total} ${invoiceDetails.moneda || ''}`.trim() : '—'}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="invoice-card">
          <h3>Datos fiscales del emisor</h3>
          <div className="invoice-grid">
            <div className="form-group">
              <label>RFC</label>
              {isReadOnly ? (
                <div className="invoice-value">{emitterInfo.rfc || '—'}</div>
              ) : (
                <input type="text" value={emitterInfo.rfc} readOnly disabled />
              )}
            </div>
            <div className="form-group">
              <label>Nombre o razón social</label>
              {isReadOnly ? (
                <div className="invoice-value">{emitterInfo.nombre || '—'}</div>
              ) : (
                <input type="text" value={emitterInfo.nombre} readOnly disabled />
              )}
            </div>
            <div className="form-group">
              <label>Régimen fiscal</label>
              {isReadOnly ? (
                <div className="invoice-value">{emitterRegimenLabel || '—'}</div>
              ) : (
                <input type="text" value={emitterRegimenLabel} readOnly disabled />
              )}
            </div>
            <div className="form-group">
              <label>Código postal</label>
              {isReadOnly ? (
                <div className="invoice-value">{emitterInfo.codigoPostal || '—'}</div>
              ) : (
                <input type="text" value={emitterInfo.codigoPostal} readOnly disabled />
              )}
            </div>
          </div>
        </div>

        <div className="invoice-card">
          <h3>Datos fiscales del receptor</h3>
          <div className="invoice-grid">
            <div className="form-group">
              <label>RFC</label>
              {isReadOnly ? (
                <div className="invoice-value">{formData.receptorRfc || '—'}</div>
              ) : (
                <input
                  type="text"
                  value={formData.receptorRfc}
                  onChange={handleChange('receptorRfc')}
                  placeholder="RFC del receptor"
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label>Nombre o razón social</label>
              {isReadOnly ? (
                <div className="invoice-value">{formData.receptorNombre || '—'}</div>
              ) : (
                <input
                  type="text"
                  value={formData.receptorNombre}
                  onChange={handleChange('receptorNombre')}
                  placeholder="Nombre del receptor"
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label>Régimen fiscal</label>
              {isReadOnly ? (
                <div className="invoice-value">{receptorRegimenLabel || '—'}</div>
              ) : (
                <select
                  value={formData.receptorRegimenFiscal}
                  onChange={handleChange('receptorRegimenFiscal')}
                  required
                >
                  <option value="">Selecciona un régimen</option>
                  {regimenFiscalCatalog.map(item => (
                    <option key={item.codigo} value={item.codigo}>
                      {item.codigo} - {item.descripcion}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Uso CFDI</label>
              {isReadOnly ? (
                <div className="invoice-value">{receptorUsoLabel || '—'}</div>
              ) : (
                <select
                  value={formData.receptorUsoCfdi}
                  onChange={handleChange('receptorUsoCfdi')}
                  required
                >
                  <option value="">Selecciona un uso</option>
                  {usoCfdiCatalog.map(item => (
                    <option key={item.codigo} value={item.codigo}>
                      {item.codigo} - {item.descripcion}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Código postal</label>
              {isReadOnly ? (
                <div className="invoice-value">{formData.receptorCodigoPostal || '—'}</div>
              ) : (
                <input
                  type="text"
                  value={formData.receptorCodigoPostal}
                  onChange={handleChange('receptorCodigoPostal')}
                  placeholder="Código postal"
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label>Email</label>
              {isReadOnly ? (
                <div className="invoice-value">{formData.receptorEmail || '—'}</div>
              ) : (
                <input
                  type="email"
                  value={formData.receptorEmail}
                  onChange={handleChange('receptorEmail')}
                  placeholder="correo@ejemplo.com"
                />
              )}
            </div>
          </div>
        </div>

        <div className="invoice-card">
          <h3>Datos del comprobante</h3>
          <div className="invoice-grid">
            {!isReadOnly && billingSerie ? (
              <div className="form-group">
                <label>Serie</label>
                <input type="text" value={billingSerie} readOnly disabled />
              </div>
            ) : null}
            {!isReadOnly ? (
              <div className="form-group">
                <label>Folio</label>
                {billingNextFolio ? (
                  <input type="text" value={billingNextFolio} readOnly disabled />
                ) : (
                  <input
                    type="text"
                    value={formData.folio}
                    onChange={handleChange('folio')}
                    placeholder="Folio"
                  />
                )}
              </div>
            ) : null}
            <div className="form-group">
              <label>Forma de pago</label>
              {isReadOnly ? (
                <div className="invoice-value">{formaPagoLabel || '—'}</div>
              ) : (
                <select
                  value={formData.formaPago}
                  onChange={handleChange('formaPago')}
                >
                  {SAT_PAYMENT_METHODS.map(item => (
                    <option key={item.code} value={item.code}>
                      {item.code} - {item.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Método de pago</label>
              {isReadOnly ? (
                <div className="invoice-value">{metodoPagoLabel || '—'}</div>
              ) : (
                <select
                  value={formData.metodoPago}
                  onChange={handleChange('metodoPago')}
                >
                  {SAT_PAYMENT_METHOD_TYPES.map(item => (
                    <option key={item.code} value={item.code}>
                      {item.code} - {item.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Lugar de expedición</label>
              {isReadOnly ? (
                <div className="invoice-value">{formData.lugarExpedicion || '—'}</div>
              ) : (
                <input
                  type="text"
                  value={formData.lugarExpedicion}
                  onChange={handleChange('lugarExpedicion')}
                  placeholder="Código postal"
                />
              )}
            </div>
          </div>
        </div>

        <div className="invoice-card">
          <h3>Conceptos</h3>
          {hasMissingCodigoSat && (
            <div className="invoice-warning">
              Algunos procedimientos no tienen clave de producto SAT. Actualiza el catálogo de procedimientos para continuar.
            </div>
          )}
          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>Procedimiento</th>
                  <th>Clave SAT</th>
                  <th>Impuestos</th>
                  <th>Cantidad</th>
                  <th>Precio unitario</th>
                  <th>Subtotal</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {procedures.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-row">Sin procedimientos</td>
                  </tr>
                ) : (
                  procedures.map(proc => (
                    <tr key={proc.id}>
                      <td data-label="Procedimiento">{proc.name}</td>
                      <td
                        data-label="Clave SAT"
                        className={procedureCatalog[proc.procedureId]?.codigoSAT ? '' : 'missing-code'}
                      >
                        {procedureCatalog[proc.procedureId]?.codigoSAT || 'Sin clave'}
                      </td>
                      <td data-label="Impuestos">
                        {(() => {
                          const taxes = procedureCatalog[proc.procedureId]?.taxes || {};
                          const ivaLabel = formatTaxLabel(taxes.iva, 'IVA');
                          const isrLabel = formatTaxLabel(taxes.isr, 'ISR');
                          const ivaAmount = getLineTaxAmount(proc, taxes.iva, taxes);
                          const isrAmount = getLineTaxAmount(proc, taxes.isr, taxes);
                          return (
                            <>
                              {ivaLabel ? (
                                <div className="tax-detail">
                                  <span>{ivaLabel}</span>
                                  <span>{currencyFormatter.format(ivaAmount)}</span>
                                </div>
                              ) : null}
                              {isrLabel ? (
                                <div className="tax-detail">
                                  <span>{isrLabel}</span>
                                  <span>{currencyFormatter.format(isrAmount)}</span>
                                </div>
                              ) : null}
                              {!ivaLabel && !isrLabel ? '—' : null}
                            </>
                          );
                        })()}
                      </td>
                      <td data-label="Cantidad">{proc.quantity}</td>
                      <td data-label="Precio unitario">{currencyFormatter.format(
                        (() => {
                          const taxes = procedureCatalog[proc.procedureId]?.taxes || {};
                          const quantity = Math.max(Number(proc.quantity || 0) || 1, 1);
                          return getLineListSubtotal(proc, taxes) / quantity;
                        })()
                      )}</td>
                      <td data-label="Subtotal">{currencyFormatter.format(getLineListSubtotal(proc, procedureCatalog[proc.procedureId]?.taxes || {}))}</td>
                      <td data-label="Total">{currencyFormatter.format(getLineTotal(proc))}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {procedures.length > 0 && (
            <div className="invoice-summary">
              <div>
                <span>Subtotal</span>
                <span>{currencyFormatter.format(totals.subtotal)}</span>
              </div>
              {totals.discount > 0 && (
                <div>
                  <span>Descuento</span>
                  <span>-{currencyFormatter.format(totals.discount)}</span>
                </div>
              )}
              {totals.iva > 0 && (
                <div>
                  <span>IVA</span>
                  <span>{currencyFormatter.format(totals.iva)}</span>
                </div>
              )}
              {totals.isr > 0 && (
                <div>
                  <span>ISR</span>
                  <span>-{currencyFormatter.format(totals.isr)}</span>
                </div>
              )}
              <div className="invoice-summary-total">
                <span>Total</span>
                <span>{currencyFormatter.format(totals.total)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="invoice-actions">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate(`/patients/${patientId}/treatment-plans`)}
            disabled={isSubmitting}
          >
            Volver
          </Button>
          {plan?.invoicedBySat ? (
            <>
              <Button
                variant="primary"
                type="button"
                disabled={isDownloadDisabled}
                onClick={() => downloadInvoiceFile('xml')}
              >
                {isSubmitting ? <span className="loading-spinner"></span> : 'Descargar XML'}
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={isDownloadDisabled}
                onClick={() => downloadInvoiceFile('pdf')}
              >
                {isSubmitting ? <span className="loading-spinner"></span> : 'Descargar PDF'}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              type="button"
              disabled={isGenerateDisabled}
              onClick={handleGenerateInvoice}
            >
              {isSubmitting ? <span className="loading-spinner"></span> : 'Generar factura'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanInvoice;
